/**
 * utils/storage.ts
 * -----------------
 * What: Safe storage facade that prefers `localStorage`, falls back to
 * `sessionStorage`, and finally to an in-memory shim when access is blocked
 * (e.g., Safari private mode) or not available.
 * Why: Ensures Supabase auth persistence and other storage needs do not crash
 * the app across environments.
 * Platform: Only imported on web; React Native builds use a different code path.
 */
// Utility wrapper to safely access web storage without throwing in restricted environments (e.g., Safari private mode).
// For native (React Native) this file won’t be imported because localStorage doesn’t exist.

// Minimal in-memory fallback for environments where both storages are unavailable or disabled.
const memoryStore: Record<string, string> = {};

function safeGet(type: 'local' | 'session') {
  try {
    return type === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function getBackend() {
  const local = safeGet('local');
  if (local) {
    try {
      const testKey = '__test_storage__';
      local.setItem(testKey, '1');
      local.removeItem(testKey);
      return local;
    } catch {}
  }
  const session = safeGet('session');
  if (session) return session;
  // Fallback to in-memory store
  return {
    getItem: (k: string) => memoryStore[k] ?? null,
    setItem: (k: string, v: string) => {
      memoryStore[k] = v;
    },
    removeItem: (k: string) => {
      delete memoryStore[k];
    },
  } as Storage;
}

export const safeStorage = getBackend(); 